import Auth from "../auth"
import { useCallback, useContext, useEffect, useState } from "react"
import { useIntl } from "react-intl"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { EMPTY, Subject, from, merge, of } from "rxjs"
import {
    catchError,
    concat,
    delay,
    filter,
    finalize,
    mapTo,
    share,
    switchMap,
    takeUntil,
    tap,
} from "rxjs/operators"
import { AuthContext } from "../context/auth"
import toastMessages from "../messages/toast"
import { ForgotPassword } from "../scenes/login/FindPasswordPanel"

interface AuthPromiseError {
    err: any
}

function ifAuthRequestError(
    x: AuthPromiseError | any,
): x is AuthPromiseError | any {
    return x && typeof x === "object" && "err" in x
}

export default function useForgotPassword() {
    const navigate = useNavigate()
    const intl = useIntl()
    const {
        email,
        setEmail,
        setPromptResetPassword,
        currentUser,
        doLogin,
        resetFlags,
    } = useContext(AuthContext)
    const [loading, setLoading] = useState(false)

    const [submitError, setSubmitError] = useState<any>({})
    const [submitEmail$] = useState(
        new Subject<{ email: string; clientMetadata: string }>(),
    )
    const submitEmail = useCallback(
        ({ email, clientMetadata }: ForgotPassword) =>
            submitEmail$.next({ email, clientMetadata }),
        [submitEmail$],
    )

    const [requestVerificationCode$] = useState(new Subject<void>())
    const requestVerificationCode = useCallback(
        () => requestVerificationCode$.next(),
        [requestVerificationCode$],
    )

    const [submitUpdateTemporaryPassword$] = useState(
        new Subject<{
            newPassword: string
            familyName?: string
            lastName?: string
        }>(),
    )
    const submitUpdateTemporaryPassword = useCallback(
        (newPassword: string, familyName?: string, lastName?: string) =>
            submitUpdateTemporaryPassword$.next({
                newPassword,
                familyName,
                lastName,
            }),
        [submitUpdateTemporaryPassword$],
    )

    const [submitResetPassword$] = useState(
        new Subject<{
            newPassword: string
            verificationCode: string
        }>(),
    )
    const submitResetPassword = useCallback(
        (newPassword: string, verificationCode: string) =>
            submitResetPassword$.next({
                newPassword,
                verificationCode,
            }),
        [submitResetPassword$],
    )

    useEffect(() => {
        const bye$ = new Subject<void>()
        const submitEmailResult$ = submitEmail$.pipe(
            tap(() => setLoading(true)),
            switchMap(({ email, clientMetadata }: ForgotPassword) =>
                from<Promise<any>>(
                    Auth.forgotPassword(email, {
                        "cf-turnstile-response": clientMetadata,
                    }),
                ).pipe(
                    tap(() => {
                        setEmail(email)
                        setPromptResetPassword(true)
                    }),
                    catchError((err) => of<AuthPromiseError>({ err })),
                    finalize(() => setLoading(false)),
                ),
            ),
            share(),
        )

        const requestVerificationCodeResult$ = requestVerificationCode$.pipe(
            tap(() => setLoading(true)),
            switchMap(() =>
                from(Auth.forgotPassword(email || "")).pipe(
                    tap(() =>
                        toast.success(
                            intl.formatMessage(toastMessages.codeSent),
                        ),
                    ),
                    switchMap(
                        () => EMPTY,
                        // TODO: Waiting for UI design for better display
                    ),
                    catchError((err) => of<AuthPromiseError>({ err })),
                    finalize(() => setLoading(false)),
                ),
            ),
            share(),
        )

        const submitUpdateTemporaryPasswordResult$ =
            submitUpdateTemporaryPassword$.pipe(
                tap(() => setLoading(true)),
                switchMap(({ newPassword, familyName, lastName }) => {
                    if (currentUser) {
                        const attributes: {
                            family_name?: string
                            last_name?: string
                        } = {}
                        if (familyName) {
                            attributes["family_name"] = familyName
                        }
                        if (lastName) {
                            attributes["last_name"] = lastName
                        }
                        return from(
                            new Promise((resolve, reject) =>
                                currentUser.completeNewPasswordChallenge(
                                    newPassword,
                                    attributes,
                                    {
                                        onFailure: reject,
                                        onSuccess: resolve,
                                    },
                                ),
                            ),
                        ).pipe(
                            mapTo(newPassword),
                            tap(() => doLogin(email || "", newPassword)),
                            catchError((err) => {
                                setLoading(false)
                                return of<AuthPromiseError>({ err })
                            }),
                        )
                    }
                    return EMPTY
                }),
                share(),
            )

        const submitResetPasswordResult$ = submitResetPassword$.pipe(
            tap(() => setLoading(true)),
            switchMap(({ newPassword, verificationCode }) =>
                from<Promise<any>>(
                    Auth.forgotPasswordSubmit(
                        email || "",
                        verificationCode,
                        newPassword,
                    ),
                ).pipe(
                    tap(() => {
                        resetFlags()
                        // TODO: Waiting for UI design
                        toast.success(
                            intl.formatMessage(toastMessages.passwordUpdated),
                        )
                        navigate("/l/login")
                    }),
                    catchError((err) => {
                        setLoading(false)
                        return of<AuthPromiseError>({ err })
                    }),
                    finalize(() => setLoading(false)),
                ),
            ),
            share(),
        )

        const submitError$ = merge(
            requestVerificationCodeResult$,
            submitEmailResult$,
            submitUpdateTemporaryPasswordResult$,
            submitResetPasswordResult$,
        ).pipe(
            filter(ifAuthRequestError),
            // TODO: Wait for UI design
            switchMap(({ err }) =>
                of(err).pipe(concat(of({}).pipe(delay(3000)))),
            ),
        )

        merge(
            submitEmailResult$,
            requestVerificationCodeResult$,
            submitUpdateTemporaryPasswordResult$,
            submitResetPasswordResult$,
        )
            .pipe(takeUntil(bye$))
            .subscribe()

        submitEmailResult$.pipe(takeUntil(bye$)).subscribe()
        requestVerificationCodeResult$.pipe(takeUntil(bye$)).subscribe()
        submitUpdateTemporaryPasswordResult$.pipe(takeUntil(bye$)).subscribe()
        submitResetPasswordResult$.pipe(takeUntil(bye$)).subscribe()
        submitError$.pipe(takeUntil(bye$)).subscribe(setSubmitError)

        return () => {
            bye$.next()
        }
    }, [
        submitEmail$,
        setPromptResetPassword,
        requestVerificationCode$,
        submitResetPassword$,
        submitUpdateTemporaryPassword$,
        setEmail,
        doLogin,
        resetFlags,
        currentUser,
        email,
        intl,
        navigate,
    ])

    return {
        loading,
        submitEmail,
        submitError,
        requestVerificationCode,
        submitUpdateTemporaryPassword,
        submitResetPassword,
    }
}
